/**
 * The arithmetic behind `/analytics/revenue`, kept out of the component so it
 * can be reasoned about — and run — on its own.
 *
 * No value imports, deliberately, on the precedent `analytics-window.ts`,
 * `analytics-charts.ts`, `analytics-users.ts`, `analytics-user-usage.ts` and
 * `analytics-application-usage.ts` set: Node 24 strips types, so a module with
 * only type imports and no JSX runs directly under `node` and the self-check at
 * the foot of this file IS its test suite.
 *
 * Four rules run through all of it, and none of them is cosmetic:
 *
 *   * **NOTHING HERE ADDS TWO CURRENCIES.** Every function below takes ONE
 *     currency entry's measures or series and answers about that entry alone.
 *     There is no parameter anywhere that could be handed the whole
 *     `currencies` list, which is the shape that makes a cross-currency total
 *     impossible to write by accident rather than merely against the rules.
 *   * **A gap is not a zero.** `arpu` is null when nobody paid, and it stays
 *     null all the way to the chart, which draws a hole and counts the holes
 *     out loud. Rendering it as 0 would say the average customer paid nothing,
 *     which is a claim about a customer base that paid.
 *   * **A rate over no attempts is null, not zero.** A day with no top-up
 *     attempts has no success rate; 0% says every attempt failed, which is the
 *     reading an operator would page somebody about.
 *   * **Money stays in MICROS here.** The screen builds the formatter from the
 *     currency the entry names — micros per major unit is 10^6 for JPY and
 *     10^8 for USD — so converting to a float in this module would put a
 *     rounding error in the chart's hidden table, which is the exact record.
 */

import type { SeriesPoint } from "@empite/ui-components";
import type {
  Amount,
  AnalyticsApplicationUsage,
  AnalyticsRevenueDay,
  AnalyticsRevenueMeasures,
} from "../admin-client";
import type { NamedSeries, SeriesBundle } from "./analytics-charts";

export type { NamedSeries, SeriesBundle };

/** One day of one currency, read as a number. Money arrives as an Amount and
 *  the micros are taken off it; a count arrives bare. */
type ReadDay = (day: AnalyticsRevenueDay) => number;

/**
 * The labels and the series, built from one currency's days.
 *
 * Private, because every exported bundle below names its own measures: a
 * caller that could pass its own reader list could pass one that mixed two
 * currency entries, and this file's first rule is that no such call can be
 * written.
 */
function bundle(
  series: readonly AnalyticsRevenueDay[],
  measures: ReadonlyArray<readonly [string, ReadDay, string?]>,
): SeriesBundle {
  return {
    labels: series.map((day) => day.day),
    series: measures.map(([name, read, color]): NamedSeries => ({
      name,
      color,
      points: series.map((day) => ({ label: day.day, value: read(day) })),
    })),
  };
}

/**
 * Settled inference revenue, what it cost upstream, and the difference.
 *
 * GROUPED and never stacked, for the reason `moneySeries` gives on the
 * dashboard: margin is sell minus cost rather than a part of either, and on a
 * written-off day it goes NEGATIVE — which is the day this screen exists to
 * surface, and which a stack cannot draw at all.
 *
 * These are the SETTLED figures, read through the settlements, and they are
 * not the same numbers as the usage rollup's billed ones for the same day —
 * that one includes usage whose settlement is still open. The card says so;
 * this function only reports what it was given.
 */
export function settledSeries(series: readonly AnalyticsRevenueDay[]): SeriesBundle {
  return bundle(series, [
    ["Inference revenue", (day) => day.inference_sell.micros],
    ["Provider cost", (day) => day.inference_cost.micros],
    ["Margin", (day) => day.margin.micros],
  ]);
}

/**
 * Money in, by route: top-ups and subscription charges.
 *
 * STACKED, and here the claim a stack makes is true — gross revenue IS the two
 * of them added together, which is how the server derives it. Refunds are
 * deliberately NOT a third segment: they are money going the other way, stored
 * as a positive magnitude and already subtracted in net revenue, so a segment
 * for them would grow the bar with the very thing that shrinks the total. They
 * are a tile beside the chart instead, and the card says the bars are gross.
 */
export function paymentsSeries(series: readonly AnalyticsRevenueDay[]): SeriesBundle {
  return bundle(series, [
    ["Top-ups", (day) => day.topup_revenue.micros],
    ["Subscription charges", (day) => day.subscription_revenue.micros],
  ]);
}

/**
 * Top-up attempts, split into the ones that took the money and the ones that
 * did not.
 *
 * A stack again, and again the claim is true by construction: an attempt
 * either settled or failed, and the two counts are what the rollup stores.
 * Succeeded sits at the BOTTOM, on `outcomeSeries`' reasoning — the failure
 * figure a reader wants is the RATE, which is a tile above, and a red
 * foundation under every bar of a healthy month says the opposite of what the
 * month did.
 */
export function topupOutcomeSeries(
  series: readonly AnalyticsRevenueDay[],
  positive: string,
  negative: string,
): SeriesBundle {
  return bundle(series, [
    ["Settled", (day) => day.topup_count, positive],
    ["Failed", (day) => day.topup_failed_count, negative],
  ]);
}

/**
 * Subscriptions started and subscriptions cancelled, per day.
 *
 * GROUPED, not stacked. The two counts are not parts of a total — nothing on
 * this platform is "the number of subscription events" — and a stack would
 * assert that they are. Side by side, the reading an operator wants is which
 * of the two bars is taller, which is exactly what the picture shows.
 */
export function subscriptionSeries(series: readonly AnalyticsRevenueDay[]): SeriesBundle {
  return bundle(series, [
    ["Started", (day) => day.new_subscriptions],
    ["Cancelled", (day) => day.cancelled_subscriptions],
  ]);
}

/** People who paid, per day. An UPPER BOUND — the rollup stores a per-(day,
 *  application, currency) distinct count and never the ids — which is why the
 *  card labels it "up to" and this function does not pretend otherwise. */
export function payingUsersSeries(series: readonly AnalyticsRevenueDay[]): SeriesPoint[] {
  return series.map((day) => ({ label: day.day, value: day.paying_users_upper_bound }));
}

/**
 * Net revenue per paying user, per day, in micros — or a HOLE.
 *
 * `arpu` is null on a day nobody paid, and it stays null: the chart primitives
 * break the line at a null and count the breaks in the accessible summary,
 * whereas a zero would be drawn as a real reading and would say the average
 * customer paid nothing on a day there was no average to take.
 */
export function arpuSeries(series: readonly AnalyticsRevenueDay[]): SeriesPoint[] {
  return series.map((day) => ({
    label: day.day,
    value: day.arpu === null ? null : day.arpu.micros,
  }));
}

/**
 * Settled top-ups over attempted ones, in [0, 1] — or null when nothing was
 * attempted.
 *
 * NEVER zero for an idle window. "No customer tried to top up" and "every
 * customer who tried was declined" are the same figure under a naive division
 * and the second one is a payment provider on fire, so the two must not
 * collapse. The caller renders the null as words.
 */
export function topupSuccessRate(
  m: Pick<AnalyticsRevenueMeasures, "topup_count" | "topup_failed_count">,
): number | null {
  const attempts = m.topup_count + m.topup_failed_count;
  return attempts > 0 ? m.topup_count / attempts : null;
}

/**
 * Whether anything at all happened in this currency over the window.
 *
 * Over the STORED figures and not the derived ones, on `settlementState`'s
 * reasoning: `margin` is sell minus cost and is zero for a break-even window,
 * `net_revenue` is zero for a top-up exactly cancelled by a refund, and
 * reading either would call both of those quiet.
 *
 * The counts are in the disjunction as well as the amounts. A window whose
 * only event was a failed top-up moved no money and is emphatically not quiet.
 */
export function moneyMoved(m: AnalyticsRevenueMeasures): boolean {
  return (
    m.topup_revenue.micros !== 0 ||
    m.subscription_revenue.micros !== 0 ||
    m.refunds.micros !== 0 ||
    m.inference_sell.micros !== 0 ||
    m.inference_cost.micros !== 0 ||
    m.topup_count !== 0 ||
    m.subscription_charge_count !== 0 ||
    m.topup_failed_count !== 0 ||
    m.paying_users_upper_bound !== 0 ||
    m.new_subscriptions !== 0 ||
    m.cancelled_subscriptions !== 0
  );
}

/**
 * What the screen is looking at, in FIVE cases rather than two.
 *
 * `loading` and `failed` are the read's own states. The other three are all
 * documents that arrived, and folding any two of them together would put a
 * sentence on the screen that is not true of one of them:
 *
 *   * `unmeasured` — the rollups do not reach this window. Every figure is a
 *     zero that was never counted, and drawing them would report an idle
 *     platform on days nobody was measuring.
 *   * `quiet` — the rollups reach it and it moved no money in any currency.
 *     `currencies` is then empty, because the sweep writes no row for a
 *     currency that saw nothing. A real reading, and a different one.
 *   * `ready` — at least one currency has something to say.
 *
 * `measured` is passed in rather than computed here so that `windowMeasured`
 * in analytics-window.ts stays the one implementation of that comparison.
 */
export type RevenueState =
  | { kind: "loading" }
  | { kind: "failed" }
  | { kind: "unmeasured" }
  | { kind: "quiet" }
  | { kind: "ready" };

export function revenueState(
  hasReport: boolean,
  hasError: boolean,
  measured: boolean,
  currencies: number,
): RevenueState {
  if (!hasReport) {
    // The error is checked SECOND. A refetch that fails leaves the previous
    // document in the cache on some queries, and a screen that showed the
    // banner instead of the figures it still holds has thrown away an answer.
    return hasError ? { kind: "failed" } : { kind: "loading" };
  }
  if (!measured) {
    return { kind: "unmeasured" };
  }
  return currencies > 0 ? { kind: "ready" } : { kind: "quiet" };
}

/**
 * Whether the answer covers fewer days than the window asked for.
 *
 * The series starts at `available_from` and not at `window.since`: before the
 * first rollup there is nothing to draw, and a zero there would be an
 * invention rather than a measurement. So a window reaching back past the
 * sweep's first day gets a TRUNCATED answer — the totals above the chart are
 * the totals of the days that exist, not of the span in the heading — and the
 * screen says so rather than letting the heading be read as the coverage.
 *
 * `until` is in the signature so that this cannot be true of a window the
 * rollups do not reach AT ALL. That window is UNMEASURED — a different state
 * with a different explanation, and one whose figures are not shown — and a
 * banner announcing a partial answer above a page that is showing none reads
 * as a contradiction. Taking the bound here rather than trusting every caller
 * to check `windowMeasured` first is what makes the contradiction unwritable.
 */
export function truncated(
  availableFrom: string | null,
  since: string,
  until: string,
): boolean {
  return availableFrom !== null && availableFrom > since && availableFrom <= until;
}

/**
 * How many days a pair of inclusive ISO dates spans, or 0 if they run
 * backwards.
 *
 * Off the DATES and not off the series, deliberately: the banner names the
 * span and the count in one sentence, and two numbers derived from two sources
 * are two numbers that can disagree in front of the reader. A series length is
 * what was DRAWN; this is what the sentence claims, and the sentence is what
 * gets quoted.
 *
 * `Date.UTC` and not the local constructor: these are rollup buckets in UTC,
 * and a browser west of Greenwich parsing them as local instants would put the
 * two ends in different days and come back one short.
 */
export function spanDays(from: string, until: string): number {
  const start = Date.parse(`${from}T00:00:00Z`);
  const end = Date.parse(`${until}T00:00:00Z`);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) {
    return 0;
  }
  // Both ends inclusive, and the division is exact: both operands are
  // midnight UTC, so the difference is a whole number of 86,400,000s with no
  // daylight-saving hour anywhere in it.
  return Math.round((end - start) / 86_400_000) + 1;
}

/* -------------------------------------------------------------------------- *
 * The per-application table.
 *
 * A SECOND read, over GET /admin/v1/analytics/applications, because the
 * revenue route reads billing.revenue_daily and nothing else — it cannot name
 * an application without reading the catalogue it is not allowed to read.
 * -------------------------------------------------------------------------- */

/**
 * What one application row's money is, in three cases.
 *
 * `withheld` is the absent `revenue` object. It cannot happen on this screen —
 * the route above it demanded `wallets:read` at the door — and it is here
 * anyway, because the alternative is a row that reports a permission boundary
 * as a business fact if the two grants ever come apart.
 *
 * `foreign` is the row this whole distinction exists for. The applications
 * report sums its money columns in ONE reporting currency, so an application
 * billing in another one reads as ZERO money — and the row's own `currency` is
 * the only thing that tells that from "earned nothing". The table prints the
 * words rather than the zeros for such a row.
 *
 * An EMPTY `currency` is not foreign: it is what the wire carries for an
 * application that settled nothing at all, which is an honest zero in whatever
 * currency you like.
 */
export type RowMoney = "withheld" | "foreign" | "reported";

export function rowMoney(
  row: Pick<AnalyticsApplicationUsage, "revenue" | "currency">,
  reportCurrency: string | undefined,
): RowMoney {
  if (row.revenue === undefined) {
    return "withheld";
  }
  if (row.currency && reportCurrency && row.currency !== reportCurrency) {
    return "foreign";
  }
  return "reported";
}

/**
 * The applications, biggest earner first.
 *
 * A client-side ordering is only honest over a COMPLETE list, and this one is:
 * the applications route has no `limit` and no `sort`, and it returns one row
 * per application in the catalogue — including every application that earned
 * nothing, which is the point of reading the catalogue rather than the rollup.
 * Re-ranking it here therefore names the same set of products in a different
 * order, unlike the top-users table, where the server ranks and then CUTS and
 * a browser that re-ranked the survivors would label them as the platform's
 * top ten.
 *
 * Ordered by SETTLED margin descending and not by revenue: this is the money
 * screen, and the question it is on the page to answer is which product is
 * worth running. Ties break on the slug, ascending, because nothing in the
 * wire order is guaranteed and two products on the same figure must not swap
 * places between two loads of the same page.
 *
 * A row whose money is withheld or foreign sorts LAST in both cases, and by
 * slug among itself: it has no figure in the reporting currency, and giving it
 * the zero it superficially reads as would rank "we cannot tell you" among the
 * products that broke even.
 */
export function rankApplications(
  rows: readonly AnalyticsApplicationUsage[],
  reportCurrency: string | undefined,
): AnalyticsApplicationUsage[] {
  const ranked = [...rows];
  ranked.sort((a, b) => {
    const aMoney = rowMoney(a, reportCurrency) === "reported";
    const bMoney = rowMoney(b, reportCurrency) === "reported";
    if (aMoney !== bMoney) {
      return aMoney ? -1 : 1;
    }
    if (aMoney && bMoney) {
      const byMargin =
        (b.revenue?.settled_margin_micros ?? 0) - (a.revenue?.settled_margin_micros ?? 0);
      if (byMargin !== 0) {
        return byMargin;
      }
    }
    const aSlug = a.application.slug;
    const bSlug = b.application.slug;
    return aSlug < bSlug ? -1 : aSlug > bSlug ? 1 : 0;
  });
  return ranked;
}

/* ------------------------------------------------------------------------ *
 * Self-check: `node apps/admin-portal/src/screens/analytics-revenue.ts`
 * ------------------------------------------------------------------------ */

/** An Amount, with the micros the check is about. The `amount` string is the
 *  server's own rendering of the same figure and is deliberately NOT what this
 *  screen draws — see `amountUnits` in revenue.tsx — so it is filled with a
 *  marker here rather than with a plausible decimal nobody would check. */
function amount(micros: number, currency = "USD"): Amount {
  return { currency, amount: `<${micros}>`, micros };
}

/** A slice with only the fields a given check reads. The cast is honest at the
 *  one place it is made: these fixtures never reach a renderer. */
function measures(fields: Partial<AnalyticsRevenueMeasures>): AnalyticsRevenueMeasures {
  return {
    topup_revenue: amount(0),
    topup_count: 0,
    subscription_revenue: amount(0),
    subscription_charge_count: 0,
    topup_failed_count: 0,
    refunds: amount(0),
    gross_revenue: amount(0),
    net_revenue: amount(0),
    inference_sell: amount(0),
    inference_cost: amount(0),
    margin: amount(0),
    arpu: null,
    paying_users_upper_bound: 0,
    new_subscriptions: 0,
    cancelled_subscriptions: 0,
    ...fields,
  };
}

function day(date: string, fields: Partial<AnalyticsRevenueMeasures>): AnalyticsRevenueDay {
  return { ...measures(fields), day: date };
}

function app(
  slug: string,
  fields: Partial<AnalyticsApplicationUsage>,
): AnalyticsApplicationUsage {
  return {
    application: { id: `id-${slug}`, slug, name: `The ${slug} app` },
    status: "active",
    series: [],
    requests: 0,
    failed_requests: 0,
    failure_rate: 0,
    input_tokens: 0,
    cached_input_tokens: 0,
    cache_write_tokens: 0,
    output_tokens: 0,
    reasoning_tokens: 0,
    total_tokens: 0,
    fallback_requests: 0,
    stream_requests: 0,
    fallback_rate: 0,
    stream_share: 0,
    distinct_users_upper_bound: 0,
    distinct_devices_upper_bound: 0,
    latency: {
      basis: "measured",
      p50_first_token_ms: null,
      p95_first_token_ms: null,
      p50_total_ms: null,
      p95_total_ms: null,
      first_token_days: 0,
      total_days: 0,
    },
    ...fields,
  };
}

function appRevenue(
  fields: Partial<NonNullable<AnalyticsApplicationUsage["revenue"]>>,
): NonNullable<AnalyticsApplicationUsage["revenue"]> {
  return {
    settled_sell_micros: 0,
    settled_cost_micros: 0,
    settled_margin_micros: 0,
    topup_micros: 0,
    subscription_charge_micros: 0,
    refund_micros: 0,
    net_payments_micros: 0,
    paying_users_upper_bound: 0,
    ...fields,
  };
}

function selfCheck(): void {
  let passed = 0;
  const check = (name: string, actual: unknown, expected: unknown): void => {
    // A replacer, because JSON.stringify DROPS an undefined-valued key and a
    // withheld money field is exactly an undefined-valued key.
    const show = (value: unknown): string =>
      JSON.stringify(value, (_key, item) => (item === undefined ? "<undefined>" : item)) ??
      "<undefined>";
    if (show(actual) !== show(expected)) {
      throw new Error(
        `analytics-revenue self-check FAILED: ${name}\n  expected ${show(expected)}\n  actual   ${show(actual)}`,
      );
    }
    passed += 1;
  };

  // ---- settledSeries ----------------------------------------------------
  // A LOSS-MAKING day, because that is the day this chart exists for: a
  // margin read as a magnitude, or clamped at zero the way a stack's parts
  // have to be, would draw the one day an operator opened the page to find as
  // an ordinary one.
  const settled = settledSeries([
    day("2026-03-01", {
      inference_sell: amount(300_000_000),
      inference_cost: amount(120_000_000),
      margin: amount(180_000_000),
    }),
    day("2026-03-02", {
      inference_sell: amount(10_000_000),
      inference_cost: amount(90_000_000),
      margin: amount(-80_000_000),
    }),
  ]);
  check("settledSeries labels the days in order", settled.labels, ["2026-03-01", "2026-03-02"]);
  check(
    "settledSeries names revenue, cost and margin in that order",
    settled.series.map((entry) => entry.name),
    ["Inference revenue", "Provider cost", "Margin"],
  );
  check(
    "settledSeries keeps a negative margin negative",
    settled.series[2]?.points.map((point) => point.value),
    [180_000_000, -80_000_000],
  );
  check(
    "settledSeries reads micros and not the rendered string",
    settled.series[0]?.points[0]?.value,
    300_000_000,
  );
  // Each series carries the day as its own point label, so the hidden table
  // can name a row without the caller re-deriving the axis.
  check("settledSeries labels every point", settled.series[1]?.points[1]?.label, "2026-03-02");
  check("settledSeries of no days is an empty bundle", settledSeries([]).labels, []);

  // ---- paymentsSeries ---------------------------------------------------
  const payments = paymentsSeries([
    day("2026-03-01", {
      topup_revenue: amount(500_000_000),
      subscription_revenue: amount(200_000_000),
      // In the fixture and in NO series: a refund segment would grow the bar
      // with the thing that shrinks the total.
      refunds: amount(700_000_000),
    }),
  ]);
  check(
    "paymentsSeries stacks the two money-in routes and nothing else",
    payments.series.map((entry) => entry.name),
    ["Top-ups", "Subscription charges"],
  );
  check(
    "paymentsSeries does not put refunds in a bar",
    payments.series.flatMap((entry) => entry.points.map((point) => point.value)),
    [500_000_000, 200_000_000],
  );

  // ---- topupOutcomeSeries -----------------------------------------------
  const topups = topupOutcomeSeries(
    [day("2026-03-01", { topup_count: 9, topup_failed_count: 1 })],
    "POSITIVE",
    "NEGATIVE",
  );
  check(
    "topupOutcomeSeries puts the settled ones on the baseline",
    topups.series.map((entry) => entry.name),
    ["Settled", "Failed"],
  );
  check(
    "topupOutcomeSeries takes the colours it was handed",
    topups.series.map((entry) => entry.color),
    ["POSITIVE", "NEGATIVE"],
  );
  check(
    "topupOutcomeSeries counts attempts, not money",
    topups.series.map((entry) => entry.points[0]?.value),
    [9, 1],
  );

  // ---- subscriptionSeries -----------------------------------------------
  const subs = subscriptionSeries([
    day("2026-03-01", { new_subscriptions: 4, cancelled_subscriptions: 7 }),
  ]);
  check(
    "subscriptionSeries names the two movements",
    subs.series.map((entry) => entry.name),
    ["Started", "Cancelled"],
  );
  check(
    "subscriptionSeries keeps them apart",
    subs.series.map((entry) => entry.points[0]?.value),
    [4, 7],
  );

  // ---- payingUsersSeries ------------------------------------------------
  check(
    "payingUsersSeries reads the upper bound",
    payingUsersSeries([
      day("2026-03-01", { paying_users_upper_bound: 12 }),
      day("2026-03-02", { paying_users_upper_bound: 0 }),
    ]),
    [
      { label: "2026-03-01", value: 12 },
      // A measured zero. Nobody paid, and the rollup counted that.
      { label: "2026-03-02", value: 0 },
    ],
  );

  // ---- arpuSeries -------------------------------------------------------
  // The rule this whole module is most careful about: a null ARPU is a day
  // with no average to take, and it must not arrive at the chart as a zero
  // the picture draws on the baseline like a real reading.
  const arpu = arpuSeries([
    day("2026-03-01", { arpu: amount(4_500_000) }),
    day("2026-03-02", { arpu: null }),
    day("2026-03-03", { arpu: amount(0) }),
  ]);
  check("arpuSeries carries a figure through", arpu[0]?.value, 4_500_000);
  check("arpuSeries turns a missing average into a HOLE", arpu[1]?.value, null);
  check("arpuSeries never invents a zero", arpu[1]?.value === 0, false);
  // And the other direction: a measured zero — everybody who paid, paid
  // nothing net, which a refund-heavy day produces — is a reading and stays
  // one rather than becoming a hole.
  check("arpuSeries keeps a measured zero", arpu[2]?.value, 0);

  // ---- topupSuccessRate -------------------------------------------------
  check("topupSuccessRate of a clean day", topupSuccessRate({ topup_count: 9, topup_failed_count: 1 }), 0.9);
  check("topupSuccessRate when everything failed", topupSuccessRate({ topup_count: 0, topup_failed_count: 5 }), 0);
  // The one that matters: no attempts is NOT a total failure. These two lines
  // are the same figure under `count / (count + failed)` without the guard.
  check("topupSuccessRate over no attempts is not a rate", topupSuccessRate({ topup_count: 0, topup_failed_count: 0 }), null);
  check("an idle window is not a failing one", topupSuccessRate({ topup_count: 0, topup_failed_count: 0 }) === 0, false);
  check("topupSuccessRate of a perfect day", topupSuccessRate({ topup_count: 3, topup_failed_count: 0 }), 1);

  // ---- moneyMoved -------------------------------------------------------
  check("an untouched window is quiet", moneyMoved(measures({})), false);
  check("a top-up is movement", moneyMoved(measures({ topup_revenue: amount(1) })), true);
  check("a refund is movement", moneyMoved(measures({ refunds: amount(1) })), true);
  // A FAILED top-up moved no money and is still not a quiet window: something
  // happened, and it is the something an operator is on this screen for.
  check("a failed top-up is not quiet", moneyMoved(measures({ topup_failed_count: 1 })), true);
  check("a cancellation is not quiet", moneyMoved(measures({ cancelled_subscriptions: 1 })), true);
  check("inference that settled is movement", moneyMoved(measures({ inference_sell: amount(5) })), true);
  // The derived fields are NOT in the disjunction, and these two prove it: a
  // break-even window has a zero margin and a top-up cancelled by an equal
  // refund has a zero net, and reading either would call both quiet.
  check(
    "a break-even window is judged on its parts",
    moneyMoved(measures({ inference_sell: amount(9), inference_cost: amount(9), margin: amount(0) })),
    true,
  );
  check(
    "a cancelled-out day is judged on its parts",
    moneyMoved(measures({ topup_revenue: amount(9), refunds: amount(9), net_revenue: amount(0), topup_count: 1 })),
    true,
  );
  // And the mirror: gross/net/margin moving on their own — which the wire
  // cannot produce — does not make a window busy, so the disjunction cannot
  // have quietly grown a derived member.
  check(
    "a derived figure alone is not movement",
    moneyMoved(measures({ gross_revenue: amount(5), net_revenue: amount(5), margin: amount(5) })),
    false,
  );

  // ---- revenueState -----------------------------------------------------
  check("no document and no error is loading", revenueState(false, false, true, 0).kind, "loading");
  check("no document and an error is a failure", revenueState(false, true, true, 0).kind, "failed");
  check("a document over unmeasured days", revenueState(true, false, false, 0).kind, "unmeasured");
  // The pair the whole state machine exists for: identical zero documents,
  // and only `measured` tells "nobody was counting" from "nothing happened".
  check("a measured window with no currencies is quiet", revenueState(true, false, true, 0).kind, "quiet");
  check("a measured window with a currency is ready", revenueState(true, false, true, 1).kind, "ready");
  // An unmeasured window stays unmeasured even if a currency row somehow
  // arrived: `available_from` is the authority on what was counted.
  check("unmeasured beats a currency row", revenueState(true, false, false, 2).kind, "unmeasured");
  // A refetch that failed while the previous document is still cached shows
  // the figures, not the banner — the screen renders the alert beside them.
  check("a document survives an error beside it", revenueState(true, true, true, 1).kind, "ready");

  // ---- truncated / coveredDays ------------------------------------------
  check("a window the rollups start inside is truncated", truncated("2026-03-10", "2026-03-01", "2026-03-31"), true);
  check("a window starting exactly at the first day is whole", truncated("2026-03-01", "2026-03-01", "2026-03-31"), false);
  check("a window entirely after the first day is whole", truncated("2026-02-01", "2026-03-01", "2026-03-31"), false);
  check("nothing measured at all is not called truncated", truncated(null, "2026-03-01", "2026-03-31"), false);
  // The one the `until` bound is in the signature for: the rollups start
  // AFTER this window ends, which is the unmeasured state and not a partial
  // answer. Without the bound the screen banners a shortened answer over a
  // page that is showing no answer at all.
  check(
    "a window the rollups start after is unmeasured, not truncated",
    truncated("2026-06-01", "2026-03-01", "2026-03-31"),
    false,
  );
  check("the first day landing on the last day is truncated", truncated("2026-03-31", "2026-03-01", "2026-03-31"), true);

  // ---- spanDays ---------------------------------------------------------
  check("spanDays counts both ends", spanDays("2026-03-01", "2026-03-03"), 3);
  check("spanDays of one day is one day", spanDays("2026-03-01", "2026-03-01"), 1);
  check("spanDays across a month boundary", spanDays("2026-02-25", "2026-03-02"), 6);
  // A leap year, because February is where an off-by-one lives.
  check("spanDays across a leap February", spanDays("2024-02-01", "2024-03-01"), 30);
  // Whole days, not hours: a DST-shifted month would come back at 30.958 and
  // round to 31 by luck rather than by construction, which is why both ends
  // are parsed as UTC instants.
  check("spanDays across a northern DST change", spanDays("2026-03-01", "2026-04-01"), 32);
  check("spanDays across a southern DST change", spanDays("2026-10-01", "2026-11-01"), 32);
  check("spanDays over a backwards pair is nothing", spanDays("2026-03-03", "2026-03-01"), 0);
  check("spanDays over a bad date is nothing", spanDays("not-a-date", "2026-03-01"), 0);

  // ---- rowMoney ---------------------------------------------------------
  check("a row with no revenue object is withheld", rowMoney({ revenue: undefined, currency: "USD" }, "USD"), "withheld");
  check("a row in the reporting currency is reported", rowMoney({ revenue: appRevenue({}), currency: "USD" }, "USD"), "reported");
  // The row this distinction exists for: it reads as zero money in the
  // report's column and it did not earn nothing.
  check("a row in another currency is foreign", rowMoney({ revenue: appRevenue({}), currency: "EUR" }, "USD"), "foreign");
  // An empty string is what the wire carries for an application that settled
  // nothing, and that is an honest zero rather than a foreign figure.
  check("a row that settled nothing is not foreign", rowMoney({ revenue: appRevenue({}), currency: "" }, "USD"), "reported");
  check("no reporting currency named makes nothing foreign", rowMoney({ revenue: appRevenue({}), currency: "EUR" }, undefined), "reported");

  // ---- rankApplications -------------------------------------------------
  // Planted in REVERSE of the expected order throughout: Array.sort is stable
  // in V8, so a fixture already in its answer's order passes with the
  // comparator deleted.
  const ranked = rankApplications(
    [
      app("zeta", { revenue: appRevenue({ settled_margin_micros: 10 }), currency: "USD" }),
      app("alpha", { revenue: appRevenue({ settled_margin_micros: 10 }), currency: "USD" }),
      app("euro", { revenue: appRevenue({ settled_margin_micros: 999 }), currency: "EUR" }),
      app("blind", { revenue: undefined, currency: "USD" }),
      app("best", { revenue: appRevenue({ settled_margin_micros: 500 }), currency: "USD" }),
      app("loss", { revenue: appRevenue({ settled_margin_micros: -400 }), currency: "USD" }),
    ],
    "USD",
  ).map((row) => row.application.slug);
  check(
    "rankApplications puts the biggest margin first and the losses last",
    ranked,
    // best 500, then the two tied at 10 by slug, then the loss at -400; the
    // foreign and the withheld rows follow, by slug, because neither has a
    // figure in this column at all.
    ["best", "alpha", "zeta", "loss", "blind", "euro"],
  );
  // A foreign row with a huge figure in its own currency does NOT outrank a
  // reported one: the column it would be ranked in is not its column.
  check("a foreign row never wins the ranking", ranked.indexOf("euro") > ranked.indexOf("loss"), true);
  check("rankApplications of nothing is nothing", rankApplications([], "USD"), []);
  // A copy, not a sort in place: the report in the query cache is shared with
  // every other card reading it, and re-ordering it under them is the kind of
  // fault that shows up two components away.
  const original = [app("b", { revenue: appRevenue({ settled_margin_micros: 1 }) }), app("a", { revenue: appRevenue({ settled_margin_micros: 2 }) })];
  rankApplications(original, undefined);
  check("rankApplications does not reorder its input", original.map((row) => row.application.slug), ["b", "a"]);

  console.log(`analytics-revenue self-check: ${passed} checks passed`);
}

if ((import.meta as unknown as { main?: boolean }).main === true) {
  selfCheck();
}
